import { useEffect, useState, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { formatSalesValue } from "../../../utils/formatUtils";
import { useOutlet } from "../../../context/OutletContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

const DATASET_COLORS = ["#FF1A1A", "#FFC300", "#000000"];

const Barchart = ({
  dineinTabs,
  setSelectedOutlet,
  text = "sm",
  setViewMode,
}) => {
  const [outlet, setOutlet] = useState([]);
  const [sales, setSales] = useState([]);
  const [bevsales, setBevSales] = useState([]);
  const [foodsales, setFoodSales] = useState([]);

  const { outletOptions } = useOutlet();
  const chartRef = useRef(null);

  const sortedData = useMemo(() => {
    return outlet
      .map((name, i) => ({
        name,
        food: Number(foodsales[i]) || 0,
        bev: Number(bevsales[i]) || 0,
        total: Number(sales[i]) || 0,
      }))
    // .sort((a, b) => b.total - a.total);
  }, [outlet, foodsales, bevsales, sales]);

  // Shared logic for both the chart bar click and the table row click
  const selectOutlet = (name) => {
    const realIndex = outletOptions.indexOf(name);
    setSelectedOutlet({ name, index: realIndex });
    window.scrollTo({ top: 0, behavior: "auto" });
    setViewMode("details");
  };

  const handleChartClick = (event) => {
    const chartInstance = chartRef.current?.chartInstance || chartRef.current;
    if (!chartInstance) return;

    const activeElements =
      chartInstance.getElementsAtEventForMode?.(event, "nearest", { intersect: true }, false) ??
      (chartInstance.getElementsAtEvent?.(event)) ??
      (chartInstance.getElementAtEvent?.(event) ? [chartInstance.getElementAtEvent(event)] : []);

    const barElement = (activeElements || []).find((el) => el.datasetIndex === 2);
    if (!barElement) return;

    selectOutlet(sortedData[barElement.index].name);
  };

  useEffect(() => {
    if (!dineinTabs?.outletwise_sales) return;

    const rows = dineinTabs.outletwise_sales;
    setOutlet(rows.map((item) => item.Outlet));
    setSales(rows.map((item) => item.TotalSales));
    setBevSales(rows.map((item) => item.beverageSale));
    setFoodSales(rows.map((item) => item.foodSale));
  }, [dineinTabs]);

  const localValueLabelsPlugin = {
    id: "localValueLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((element, index) => {
          const value = dataset.data[index];
          if (value == null) return;

          const position = element.tooltipPosition();

          ctx.save();
          ctx.fillStyle = DATASET_COLORS[datasetIndex] || "#000";
          ctx.font = "600 11px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(`${formatSalesValue(value)}`, position.x, position.y - 6);
          ctx.restore();
        });
      });
    },
  };

  const data = useMemo(() => {
    const labels = sortedData.map((item) => item.name);
    const pick = (key) => sortedData.map((item) => item[key]);

    return {
      labels,
      datasets: [
        {
          type: "line",
          label: "Beverage Sales",
          data: pick("bev"),
          borderColor: DATASET_COLORS[0],
          backgroundColor: DATASET_COLORS[0],
          tension: 0.3,
          pointRadius: 4,
          yAxisID: "y",
          order: 1,
          borderWidth: 3,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        },
        {
          type: "line",
          label: "Food Sales",
          data: pick("food"),
          borderColor: DATASET_COLORS[1],
          backgroundColor: DATASET_COLORS[1],
          tension: 0.3,
          pointRadius: 4,
          yAxisID: "y",
          order: 2,
          borderWidth: 3,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        },
        {
          type: "bar",
          label: "Total Sales",
          data: pick("total"),
          backgroundColor: "#059142",
          borderRadius: 6,
          yAxisID: "y",
          order: 3,
        },
      ],
    };
  }, [sortedData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { padding: 20 },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: true,
        backgroundColor: "rgba(0,0,0,0.85)",
        padding: 12,
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 13 },
        callbacks: {
          title: (context) => context[0].label,
          label: (context) =>
            `${context.dataset.label || ""}: ${formatSalesValue(context.raw ?? 0)}`,
          labelColor: (context) => {
            const color = context.dataset.borderColor || context.dataset.backgroundColor;
            return {
              borderColor: color,
              backgroundColor: color,
              borderWidth: 2,
              borderRadius: 2,
            };
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            return window.innerWidth < 640 ? label.split(",")[1] : label;
          },
        },
      },
      y: {
        beginAtZero: true,
        grace: "10%",
        ticks: { callback: (val) => `${formatSalesValue(val)}` },
        title: { display: true, text: "Total Sales" },
      },
    },
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-3 lg:p-4 w-full">
      {/* Header */}
      <div className="mb-3">
        <h2 className="text-lg lg:text-xl font-semibold whitespace-nowrap text-gray-800 text-center">
          Sales by Outlet
        </h2>
        <p className="text-sm text-gray-500 text-center">
          Beverage vs Food vs Total comparison
        </p>
      </div>

      {/* Chart */}
      <div className="w-full lg:w-auto h-[350px] lg:h-[363px] mx-auto">
        <Chart
          ref={chartRef}
          type="bar"
          data={data}
          options={options}
          plugins={[localValueLabelsPlugin]}
          onClick={handleChartClick}
        />
      </div>

      {/* Outlet Sales Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full border border-gray-200 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-3 lg:px-4 py-2 text-left font-semibold whitespace-nowrap">
                Outlet
              </th>
              <th className="border px-3 lg:px-4 py-2 text-right font-semibold whitespace-nowrap">
                Beverage Sales
              </th>
              <th className="border px-3 lg:px-4 py-2 text-right font-semibold whitespace-nowrap">
                Food Sales
              </th>
              <th className="border px-3 lg:px-4 py-2 text-right font-semibold whitespace-nowrap">
                Total Sales
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td
                  className="border px-3 lg:px-4 py-2 font-semibold whitespace-nowrap cursor-pointer hover:underline"
                  onClick={() => selectOutlet(item.name)}
                >
                  {item.name}
                </td>
                <td className="border px-3 lg:px-4 py-2 text-right whitespace-nowrap">
                  {formatSalesValue(item.bev)}
                </td>
                <td className="border px-3 lg:px-4 py-2 text-right whitespace-nowrap">
                  {formatSalesValue(item.food)}
                </td>
                <td className="border px-3 lg:px-4 py-2 text-right font-semibold whitespace-nowrap">
                  {formatSalesValue(item.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Barchart;